import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { GridFilterInputValueProps, GridFilterOperator } from "@mui/x-data-grid-premium";
import { useTranslation } from "@privacy/translations";
import type { Option } from "../dynamicForm";

export const HAS_OPTION_FILTER_OPERATOR = "hasOption";

const isOption = (value: unknown): value is Option =>
  typeof value === "object" &&
  value !== null &&
  "value" in value &&
  typeof value.value === "string" &&
  "label" in value &&
  typeof value.label === "string";

const toOptions = (value: unknown): Array<Option> => (Array.isArray(value) && value.every(isOption) ? value : []);

export const hasOptionFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: HAS_OPTION_FILTER_OPERATOR,
  getApplyFilterFn: filterItem => {
    if (!filterItem.value) return null;
    return (_, row) => toOptions(row[filterItem.field]).some(option => option.value === filterItem.value);
  },
  InputComponent: HasOptionFilterInput,
});

const HasOptionFilterInput = ({ item, applyValue, focusElementRef, apiRef }: GridFilterInputValueProps) => {
  const { t } = useTranslation();
  const options = [
    ...new Map(
      [...apiRef.current.getRowModels().values()]
        .flatMap(row => toOptions(row[item.field]))
        .map(option => [option.value, option])
    ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};
